import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useImmerReducer } from "use-immer";
import { toast } from "react-toastify";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import ProjectFilePlanReducer from "./project-file-plan-reducer.ts";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";
import ENDPOINTS from "@/services/endpoints.ts";
import MEASURE_ENDPOINTS from "@/services/endpoints/measure-endpoints.ts";

import {fetchItem, postItem} from "@/queries/queries-helper.ts";

import PathElementDto from "@/models/interfaces/dtos/path-element-dto.ts";
import MeasureDto from "@/models/interfaces/dtos/measure/get/measure-dto.ts";
import { ProjectFilePlanContext, ProjectFilePlanContextProps } from "./project-file-plan-context-props.ts";
import ProjectFilePlanReducerAction from "./project-file-plan-reducer-action.ts";
import PlanFileData from "@/pages/takeoff/context/project-measure/models/plan-file-data.ts";
import FragmentRecord from "@/pages/takeoff/context/project-measure/models/fragment-record.ts";

interface ProjectFilePlanContextProviderProps extends React.PropsWithChildren {
    fileIdLeft: string;
    fileIdRight: string | undefined;
    setFileIdRight: (id: string | undefined) => void;
}

const ProjectFilePlanContextProvider: React.FC<ProjectFilePlanContextProviderProps> = ({ fileIdLeft, fileIdRight, setFileIdRight, children }) => {
    const { project } = useProjectContext();

    const { data: projectData, getPlanFileData, invalidateProjectReferences } = useProjectOrganisationContext();

    const { building: buildingLeft, storey: storeyLeft, file: fileLeft } = useMemo(() => {
        if (projectData == null) return undefined;
        return projectData.findBuildingStoreyAndFileFromFileId(fileIdLeft);
    }, [fileIdLeft, projectData]) ?? { storeyLeft: undefined, fileLeft: undefined };

    const { building: buildingRight, storey: storeyRight, file: fileRight } = useMemo(() => {
        if (projectData == null || fileIdRight == null) return undefined;
        return projectData.findBuildingStoreyAndFileFromFileId(fileIdRight);
    }, [fileIdRight, projectData]) ?? { storeyRight: undefined, fileRight: undefined };    

    const planFileDataLeft = useMemo(() => 
        getPlanFileData(storeyLeft?.id, fileIdLeft)?.clone() ?? null
    , [storeyLeft?.id, fileIdLeft, getPlanFileData]);
    const [dataLeft, dispatchLeft] = useImmerReducer<PlanFileData, ProjectFilePlanReducerAction>(
        ProjectFilePlanReducer,
        planFileDataLeft ?? new PlanFileData()
    );
    useEffect(() => {
        if (!planFileDataLeft) return;

        dispatchLeft({
            type: 'reset',
            data: planFileDataLeft
        });
    }, [planFileDataLeft, dispatchLeft]);

    const fileDataRight = useMemo(() => {
        if (!fileIdRight || !storeyRight) return null;
        return getPlanFileData(storeyRight.id, fileIdRight)?.clone() ?? null;
    }, [storeyRight, fileIdRight, getPlanFileData]);
    const [dataRight, dispatchRight] = useImmerReducer<PlanFileData, ProjectFilePlanReducerAction>(
        ProjectFilePlanReducer,
        fileDataRight ?? new PlanFileData()
    );
    useEffect(() => {
        if (!fileIdRight || !fileDataRight) return;

        dispatchRight({
            type: 'reset',
            data: fileDataRight
        });
    }, [fileIdRight, fileDataRight, dispatchRight]);

    const [fragsDataLeft, setFragsDataLeft] = useState<FragmentRecord[] | undefined>(undefined);
    const [fragsDataRight, setFragsDataRight] = useState<FragmentRecord[] | undefined>(undefined);

    useEffect(() => {
        if (!buildingLeft || !storeyLeft || !fileLeft) return;

        const loadFiles = async () => {
            const frags = new Array<FragmentRecord>();
            const storeyFragments = await fetchItem<PathElementDto[]>(
                ENDPOINTS.PROJECT_MEASURE_3D_FILES(project.id, project.baamerRevisionId, buildingLeft.id, storeyLeft.id)
            ) ?? [];
            storeyFragments.forEach(fragData => frags.push(new FragmentRecord(fragData.name, fragData.path, fileLeft.origin, fileLeft.sitePosition)));
            setFragsDataLeft(frags);
        };

        loadFiles();
    }, [buildingLeft, storeyLeft, fileLeft, project]);

    useEffect(() => {
        if (!buildingRight || !storeyRight || !fileRight) return;

        const loadFiles = async () => {
            const frags = new Array<FragmentRecord>();
            const storeyFragments = await fetchItem<PathElementDto[]>(
                ENDPOINTS.PROJECT_MEASURE_3D_FILES(project.id, project.baamerRevisionId, buildingRight.id, storeyRight.id)
            ) ?? [];
            storeyFragments.forEach(fragData => frags.push(new FragmentRecord(fragData.name, fragData.path, fileRight.origin, fileRight.sitePosition)));
            setFragsDataRight(frags);
        };

        loadFiles();
    }, [buildingRight, storeyRight, fileRight, project]);

    const urlFileLeft = useMemo(() => ENDPOINTS.VIEWER_PDF_PREVIEW(project.id, `${project.projectOrganisation.id}/${project.id}/${fileLeft?.relativePath}`), [project.projectOrganisation.id, project.id, fileLeft?.relativePath]);

    const urlFileRight = useMemo(() => {
        if (fileRight?.relativePath.toLocaleLowerCase().endsWith(".pdf"))
            return ENDPOINTS.VIEWER_PDF_PREVIEW(project.id, `${project.projectOrganisation.id}/${project.id}/${fileRight?.relativePath}`);
        if (fileRight?.relativePath.toLocaleLowerCase().endsWith(".ifc"))
            return ENDPOINTS.VIEWER_FILE_SAAS(`${project.projectOrganisation.id}/${project.id}/${fileRight?.relativePath}`);
        return undefined;
    }, [project.projectOrganisation.id, project.id, fileRight?.relativePath]);

    const [isSaving, setIsSaving] = useState(false);

    const saveCurrentFileData = useCallback(async (): Promise<boolean> => {
        if (!buildingLeft || !fileLeft || !storeyLeft || !storeyRight) return false;

        setIsSaving(true);

        try {
            await postItem<MeasureDto[]>(MEASURE_ENDPOINTS.COPY_STOREY_MEASURES(storeyLeft.id, storeyRight.id), {})
                .then(async () => {
                    const frags = new Array<FragmentRecord>();
                    const storeyFragments = await fetchItem<PathElementDto[]>(
                        ENDPOINTS.PROJECT_MEASURE_3D_FILES(project.id, project.baamerRevisionId, buildingLeft.id, storeyLeft.id)
                    ) ?? [];
                    storeyFragments.forEach(fragData => frags.push(new FragmentRecord(fragData.name, fragData.path, fileLeft.origin, fileLeft.sitePosition)));
                    setFragsDataLeft(frags);

                    await invalidateProjectReferences();

                    dispatchLeft({
                        type: 'set-dirty-flag',
                        isDirty: false
                    });

                    toast.success('Copie réussie');
                })
        } catch (error) {
            if (error instanceof Error)
                toast.error(error.message, {autoClose: false});
            return false;
        } finally {
            setIsSaving(false);
        }
        
        return true;
    }, [buildingLeft, fileLeft, storeyLeft, storeyRight, project, dispatchLeft, invalidateProjectReferences]);

    const props: ProjectFilePlanContextProps = useMemo(() => ({
        planFileDataLeft: dataLeft,
        planFileDataRight: dataRight,
        
        urlFileLeft,
        urlFileRight,

        buildingLeft,
        buildingRight,

        storeyLeft,
        storeyRight,
        
        fileLeft,
        fileRight,

        fragsDataLeft,
        fragsDataRight,
        
        isDirty: dataLeft.isDirty || dataRight.isDirty,
        isSaving,

        setFileIdRight,

        dispatchLeft,
        dispatchRight,

        saveCurrentFileData,
    }), [dataLeft, dataRight, urlFileLeft, urlFileRight, buildingLeft, buildingRight, storeyLeft, storeyRight, fileLeft, fileRight, fragsDataLeft, fragsDataRight, isSaving, setFileIdRight, dispatchLeft, dispatchRight, saveCurrentFileData]);

    return (
        <ProjectFilePlanContext.Provider value={props}>
            {children}
        </ProjectFilePlanContext.Provider>
    )
}

export default ProjectFilePlanContextProvider;